import type { CSSProperties } from "react";
import type { LogEngagement, RequestLogDetail, RequestLogEntry } from "@/types/dashboard";
import { logSourceBucket, logSourceLabel, statusKind, truncateText } from "@/utils/format";

export { logSourceBucket, logSourceLabel, statusKind };

export function fmtLogTime(ts: number): string {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleTimeString(undefined, { hour12: false });
  } catch {
    return "—";
  }
}

export function fmtEngagementTime(ms: number): string {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return "—";
  if (n < 1000) return `${Math.round(n)}ms`;
  const s = Math.floor(n / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rs = s % 60;
  return `${m}m${rs ? ` ${rs}s` : ""}`;
}

export function countryFlagUrl(code: string): string {
  const c = String(code || "").toLowerCase();
  if (!/^[a-z]{2}$/.test(c)) return "";
  return `https://hatscripts.github.io/circle-flags/flags/${c}.svg`;
}

export function countryName(code: string): string {
  const c = String(code || "").trim().toUpperCase();
  if (!c || c === "??") return "";
  try {
    const dn = new Intl.DisplayNames(["en"], { type: "region" });
    return dn.of(c) || c;
  } catch {
    return c;
  }
}

export function maskIpAddress(ip: string): string {
  const raw = String(ip || "").trim();
  if (!raw || raw === "—") return raw || "—";
  const noPort = raw.replace(/^\[|\]$/g, "").split("%")[0];
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(noPort)) {
    const parts = noPort.split(".");
    return `${parts[0]}.${parts[1]}.${parts[2]}.0`;
  }
  if (noPort.includes(":")) {
    const expanded = noPort.split("::");
    const left = (expanded[0] || "").split(":").filter(Boolean);
    return `${left.slice(0, 3).join(":") || "0000:0000:0000"}::/48`;
  }
  return raw;
}

export function visitorColorSeed(input: string): number {
  const s = String(input || "unknown");
  let h = 2166136261;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export function visitorGradientStyle(input: string): CSSProperties {
  const seed = visitorColorSeed(input);
  const palette: [number, number][] = [
    [214, 292],
    [142, 204],
    [18, 334],
    [48, 173],
    [262, 338],
    [188, 258],
    [6, 44],
    [96, 168],
    [226, 18],
    [286, 36],
    [158, 312],
    [202, 88]
  ];
  const pair = palette[seed % palette.length];
  const angle = 125 + ((seed >>> 8) % 70);
  return {
    background:
      `linear-gradient(${angle}deg, hsl(${pair[0]} 92% 58%), hsl(${pair[1]} 90% 56%))`
  };
}

export function requestLogPublicUrl(e: RequestLogEntry, activeSite: string): string {
  try {
    const rawPath = String(e?.url || "/");
    const rawLow = rawPath.toLowerCase();
    if (rawLow.startsWith("https://") || rawLow.startsWith("http://")) return rawPath;
    const host = String(e?.host || e?.domain || e?.site || activeSite || "").trim();
    if (!host) return rawPath;
    let bareHost = host;
    const hostLow = bareHost.toLowerCase();
    if (hostLow.startsWith("https://")) bareHost = bareHost.slice(8);
    else if (hostLow.startsWith("http://")) bareHost = bareHost.slice(7);
    bareHost = bareHost.split("/")[0];
    const path = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
    return `https://${bareHost}${path}`;
  } catch {
    return String(e?.url || "/");
  }
}

export function requestLogPageTitle(
  e: RequestLogEntry,
  _activeSite: string,
  titlesByPath: Record<string, string>
): string {
  try {
    const direct = e?.pageTitle || e?.title;
    if (direct && String(direct).trim()) return String(direct).trim();
    const path = String(e?.url || "/").split("?")[0] || "/";
    const t = titlesByPath[path] || titlesByPath[String(e?.url || "/")];
    if (t && String(t).trim()) return String(t).trim();
  } catch { /* ignore */ }
  return "";
}

export function isDashboardPreviewLogUrl(rawUrl: string): boolean {
  try {
    const u = new URL(String(rawUrl || "/"), "https://example.invalid");
    if (
      u.searchParams.has("__sitewrap_dash_preview") ||
      u.searchParams.has("__sitewrap_scroll_y") ||
      u.searchParams.has("__sitewrap_scroll_x") ||
      u.searchParams.has("__sitewrap_preview_ab") ||
      u.searchParams.has("__sitewrap_embed") ||
      u.searchParams.has("__crea_dash_preview")
    ) {
      return true;
    }
    const p = u.pathname || "/";
    if (p.startsWith("/__sitewrap_dash_preview")) return true;
    if (p.startsWith("/__sitewrap_embed")) return true;
    return false;
  } catch {
    return /__sitewrap_dash_preview|__sitewrap_scroll_[xy]|__sitewrap_embed/.test(String(rawUrl || ""));
  }
}

export function isSystemRequestLogEntry(e: RequestLogEntry): boolean {
  try {
    if (isDashboardPreviewLogUrl(String(e?.url || ""))) return true;
    const path = (String(e?.url || "").split("?")[0] || "/");
    if (path.startsWith("/dashboard")) return true;
    if (path.startsWith("/socket.io")) return true;
    if (path.startsWith("/__mp/")) return true;
    if (path.startsWith("/auth/") || path.startsWith("/__auth/")) return true;
    if (path.startsWith("/__sitewrap_")) return true;
    if (path.startsWith("/__gate/")) return true;
    if (path === "/__mirror") return true;
    if (path.startsWith("/assets/tracking")) return true;
    if (path.startsWith("/assets/rocket-loader")) return true;
    if (path === "/assets/vendor/mp.js") return true;
    if (path === "/assets/wp-admin-tools.js") return true;
    if (path === "/assets/gear-panel.js") return true;
    return false;
  } catch {
    return false;
  }
}

export function isMobileUserAgent(ua: string): boolean {
  return /mobi|android|iphone|ipad|ipod|windows phone|blackberry|webos/i.test(String(ua || ""));
}

export function engagementFromEntry(entry: RequestLogEntry | RequestLogDetail | null): LogEngagement | null {
  if (!entry) return null;
  const hasStored =
    entry.timeOnPageMs != null || entry.interactions != null || entry.scrollDepthPct != null;
  if (!hasStored) return null;
  return {
    timeOnPageMs: Number(entry.timeOnPageMs) || 0,
    interactions: Number(entry.interactions) || 0,
    scrollDepthPct: Number(entry.scrollDepthPct) || 0,
    updatedAtMs: Number(entry.engagementAtMs) || 0
  };
}

export function mergeEngagement(stored: LogEngagement | null, live: LogEngagement | null): LogEngagement | null {
  if (!stored) return live;
  if (!live) return stored;
  return {
    timeOnPageMs: Math.max(stored.timeOnPageMs, live.timeOnPageMs),
    interactions: Math.max(stored.interactions, live.interactions),
    scrollDepthPct: Math.max(stored.scrollDepthPct, live.scrollDepthPct),
    updatedAtMs: Math.max(stored.updatedAtMs, live.updatedAtMs)
  };
}

export function logEngagementKey(userId: string, url: string): string {
  return `${String(userId || "")}|${String(url || "")}`;
}

export function getLogEngagement(
  logId: string,
  entry: RequestLogEntry | null,
  byId: Record<string, LogEngagement>,
  _byUserUrl?: Record<string, LogEngagement>
): LogEngagement | null {
  // Per-row only. Falling back to user+url smears the current visit onto earlier
  // views of the same path (browser-cache revisits).
  const live = logId ? (byId[logId] || null) : null;
  return mergeEngagement(engagementFromEntry(entry), live);
}

export type LogEngagementEvent = {
  logId?: number | string;
  userId?: string;
  url?: string;
  host?: string;
  site?: string;
  timeOnPageMs?: number;
  interactions?: number;
  scrollDepthPct?: number;
  updatedAtMs?: number;
};

export function engagementPayloadFromEvent(
  ev: LogEngagementEvent,
  prev?: LogEngagement | null
): LogEngagement {
  return {
    timeOnPageMs: Math.max(Number(prev?.timeOnPageMs) || 0, Number(ev.timeOnPageMs) || 0),
    interactions: Math.max(Number(prev?.interactions) || 0, Number(ev.interactions) || 0),
    scrollDepthPct: Math.max(Number(prev?.scrollDepthPct) || 0, Number(ev.scrollDepthPct) || 0),
    updatedAtMs: Number(ev.updatedAtMs) || Date.now()
  };
}

export function applyLogEngagementEvent(
  ev: LogEngagementEvent,
  byId: Record<string, LogEngagement>,
  byUserUrl: Record<string, LogEngagement>
): { byId: Record<string, LogEngagement>; byUserUrl: Record<string, LogEngagement> } {
  const nextById = { ...byId };
  const nextByUserUrl = { ...byUserUrl };
  const logId = ev.logId != null ? String(ev.logId) : "";
  const payload = engagementPayloadFromEvent(
    ev,
    logId ? nextById[logId] : null
  );
  if (logId) nextById[logId] = payload;
  if (ev.userId && ev.url) {
    const k = logEngagementKey(ev.userId, ev.url);
    nextByUserUrl[k] = engagementPayloadFromEvent(ev, nextByUserUrl[k]);
  }
  return { byId: nextById, byUserUrl: nextByUserUrl };
}

export function hydrateEngagementMaps(entries: RequestLogEntry[]): {
  byId: Record<string, LogEngagement>;
  byUserUrl: Record<string, LogEngagement>;
} {
  const byId: Record<string, LogEngagement> = {};
  const byUserUrl: Record<string, LogEngagement> = {};
  for (const e of entries) {
    const stored = engagementFromEntry(e);
    if (!stored) continue;
    const id = e.id != null ? String(e.id) : "";
    if (id) byId[id] = mergeEngagement(byId[id], stored) ?? stored;
    if (e.userId && e.url) {
      const k = logEngagementKey(e.userId, e.url);
      byUserUrl[k] = mergeEngagement(byUserUrl[k], stored) ?? stored;
    }
  }
  return { byId, byUserUrl };
}

export function patchRequestLogEngagement(
  entry: RequestLogEntry,
  eng: LogEngagement
): RequestLogEntry {
  return {
    ...entry,
    timeOnPageMs: Math.max(Number(entry.timeOnPageMs) || 0, eng.timeOnPageMs),
    interactions: Math.max(Number(entry.interactions) || 0, eng.interactions),
    scrollDepthPct: Math.max(Number(entry.scrollDepthPct) || 0, eng.scrollDepthPct),
    engagementAtMs: Math.max(Number(entry.engagementAtMs) || 0, eng.updatedAtMs)
  };
}

export function shouldShowLogEngagement(
  entry: RequestLogEntry,
  eng: LogEngagement | null
): boolean {
  return Boolean(eng && entry.group === "Page" && !entry.isBot);
}

export function formatReferrerShort(url: string): string {
  const u = String(url || "").trim();
  if (!u) return "";
  try {
    const parsed = new URL(u);
    const host = parsed.hostname || "";
    const path = (parsed.pathname || "/") + (parsed.search || "");
    if (!path || path === "/") return host;
    return host + path;
  } catch {
    return u.length > 72 ? `${u.slice(0, 72)}…` : u;
  }
}

export function logReqReferer(d: RequestLogDetail): string {
  try {
    const h = d?.req?.headers;
    if (!h || typeof h !== "object") return "";
    return String(h.referer || h.Referer || h.referrer || h.Referrer || "").trim();
  } catch {
    return "";
  }
}

export function requestLogIpIsBanned(ip: string, blocklist: string[]): boolean {
  const s = String(ip || "").trim();
  if (!s || !Array.isArray(blocklist)) return false;
  for (const raw of blocklist) {
    const p = String(raw || "").trim();
    if (!p) continue;
    if (p === s) return true;
    if ((p.endsWith(".") || p.endsWith(":")) && s.startsWith(p)) return true;
    if (p.includes("/")) {
      const base = p.split("/")[0].trim();
      if (base && s.startsWith(base)) return true;
    }
  }
  return false;
}

export function safeStringify(obj: unknown, max = 120_000): string {
  try {
    const s = JSON.stringify(obj ?? {}, null, 2);
    if (typeof s === "string" && s.length > max) return `${s.slice(0, max)}\n… (truncated)`;
    return s || "{}";
  } catch {
    return '{ "error": "failed to stringify" }';
  }
}

export function logFilterHaystack(
  e: RequestLogEntry,
  activeSite: string,
  email: string,
  pageTitle: string
): string {
  return [
    e.method,
    e.url,
    pageTitle,
    String(e.status),
    e.group || "",
    e.isBot ? "bot" : "human",
    logSourceBucket(e),
    e.source || "",
    e.host || "",
    e.ip || "",
    e.staging ? "staging" : "",
    e.rocketLoader ? "rocket rocket-loader rocketloader" : "",
    email
  ].join(" ").toLowerCase();
}

export function truncateLogText(s: string, max = 90): string {
  return truncateText(s, max);
}

export type RequestLogStatusBand = "2xx" | "3xx" | "4xx" | "5xx";

export function requestLogStatusBand(status: unknown): RequestLogStatusBand {
  const n = Number(status);
  if (!Number.isFinite(n)) return "5xx";
  if (n >= 200 && n < 300) return "2xx";
  if (n >= 300 && n < 400) return "3xx";
  if (n >= 400 && n < 500) return "4xx";
  return "5xx";
}

export function requestLogMethodKey(method: unknown): string {
  const m = String(method || "GET").toUpperCase();
  if (m === "GET" || m === "POST" || m === "PUT" || m === "PATCH" || m === "DELETE" || m === "HEAD") return m;
  return "other";
}

export function requestLogSourceFilterLabel(bucket: string): string {
  if (bucket === "nginx") return "SW Edge";
  if (bucket === "node") return "SW App";
  if (bucket === "origin") return "Origin";
  if (bucket === "browser_cache") return "Browser cache";
  if (bucket === "cloudfront") return "CloudFront";
  return bucket || "Other";
}
